<template>
  <div class="retention-panel">
    <div class="retention-head">
      <strong>Retention</strong>
      <span class="muted small">
        From years attended — canceled years and faculty-only records are out.
        Mix numbers are for festival {{ year }}.
      </span>
    </div>
    <p v-if="loading" class="muted small">Loading dancers…</p>
    <p v-else-if="error" class="error--text small">{{ error }}</p>
    <template v-else-if="stats">
      <div class="retention-grid">
        <div class="ret-stat">
          <div class="ret-label">Unique IABF-ers</div>
          <div class="ret-num">{{ stats.uniqueCount }}</div>
        </div>
        <div class="ret-stat">
          <div class="ret-label">Avg years attended</div>
          <div class="ret-num">{{ avgLabel }}</div>
        </div>
        <div class="ret-stat">
          <div class="ret-label">{{ year }} dancers</div>
          <div class="ret-num">{{ stats.yoy.thisYearCount }}</div>
          <div class="ret-sub">{{ stats.yoy.newCount }} first year · {{ stats.yoy.returningCount }} returning</div>
        </div>
        <div class="ret-stat">
          <div class="ret-label">Returned from {{ stats.yoy.priorYear }}</div>
          <div class="ret-num">{{ formatRate(stats.yoy.rate) }}</div>
          <div class="ret-sub">{{ stats.yoy.returnedCount }} of {{ stats.yoy.priorCount }}</div>
        </div>
        <div class="ret-stat">
          <div class="ret-label">2nd-year conversion</div>
          <div class="ret-num">{{ formatRate(stats.secondYear.rate) }}</div>
          <div class="ret-sub">
            {{ stats.secondYear.returned }} of {{ stats.secondYear.starters }}
            whose first year was {{ stats.secondYear.firstYear }}
          </div>
        </div>
        <div class="ret-stat">
          <div class="ret-label">Did not return after {{ stats.dropoff.lastYear }}</div>
          <div class="ret-num">{{ stats.dropoff.count }}</div>
        </div>
        <div class="ret-stat">
          <div class="ret-label">Came back after a gap</div>
          <div class="ret-num">{{ stats.reactivatedCount }}</div>
        </div>
        <div class="ret-stat">
          <div class="ret-label">{{ year }} loyalty mix</div>
          <div class="ret-num ret-num-sm">
            {{ stats.loyalty.first }} first · {{ stats.loyalty.occasional }} 2–3 yr · {{ stats.loyalty.regular }} 4+
          </div>
        </div>
      </div>

      <p class="retention-tenure muted small">
        <strong>Tenure:</strong>
        {{ tenureLabel }}
      </p>

      <div v-if="stats.cohortSurvival.length" class="retention-table-wrap">
        <table class="retention-table">
          <thead>
            <tr>
              <th>First year</th>
              <th class="num-col">Started</th>
              <th
                v-for="y in stats.allYears"
                :key="'h-' + y"
                class="num-col"
              >
                {{ y }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in stats.cohortSurvival" :key="row.startYear">
              <td>{{ row.startYear }}</td>
              <td class="num-col">{{ row.startCount }}</td>
              <td
                v-for="y in stats.allYears"
                :key="row.startYear + '-' + y"
                class="num-col"
              >
                <template v-if="y < row.startYear">—</template>
                <template v-else>{{ pctCell(row, y) }}</template>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="muted small">
          Each row is a first-year cohort. Cells are how many of that group
          attended that later festival (count and %).
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { db } from '@/firebase'
import { IABF_PEOPLE_COLLECTION } from '@/config/iabf-event'
import { computeRetention, formatRate } from '@/utils/iabf-retention'

export default {
  name: 'RetentionPanel',
  props: {
    year: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      error: '',
      people: []
    }
  },
  computed: {
    stats () {
      if (!this.people.length) {
        return null
      }
      return computeRetention(this.people, { focusYear: this.year })
    },
    avgLabel () {
      if (!this.stats) {
        return '—'
      }
      return this.stats.avgTenure.toFixed(1)
    },
    tenureLabel () {
      if (!this.stats || !this.stats.tenureRows.length) {
        return 'None on file'
      }
      return this.stats.tenureRows.map((row) => {
        const noun = row.years === 1 ? '1 year' : row.years + ' years'
        return row.count + ' at ' + noun
      }).join(' · ')
    }
  },
  created () {
    this.loadPeople()
  },
  methods: {
    formatRate,
    pctCell (row, year) {
      const n = row.returned[year] || 0
      if (!row.startCount) {
        return '0'
      }
      const pct = Math.round((n / row.startCount) * 100)
      return n + ' (' + pct + '%)'
    },
    async loadPeople () {
      this.loading = true
      this.error = ''
      try {
        const snap = await db.collection(IABF_PEOPLE_COLLECTION).get()
        this.people = snap.docs.map((doc) => ({ id: doc.id, ...(doc.data() || {}) }))
      } catch (e) {
        console.error('retention people load failed:', e)
        this.error = (e && e.message) || 'Could not load dancers.'
      }
      this.loading = false
    }
  }
}
</script>

<style scoped>
.retention-panel {
  background: #fbf8f3;
  border: 1px solid #e6e1d4;
  border-radius: 6px;
  padding: 12px 14px;
  margin: 14px 0 18px;
}
.retention-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.retention-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 10px 14px;
  margin-bottom: 12px;
}
.ret-stat {
  background: #fff;
  border: 1px solid #eee8dc;
  border-radius: 6px;
  padding: 10px 12px;
}
.ret-label {
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7a7468;
}
.ret-num {
  font-size: 22px;
  font-weight: 700;
  color: #2c2a26;
  line-height: 1.2;
  margin-top: 4px;
}
.ret-num-sm {
  font-size: 14px;
  font-weight: 600;
}
.ret-sub {
  font-size: 12px;
  color: #7a7468;
  margin-top: 2px;
}
.retention-tenure {
  margin: 0 0 12px;
}
.retention-table-wrap {
  overflow-x: auto;
}
.retention-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
}
.retention-table th,
.retention-table td {
  border: 1px solid #eee8dc;
  padding: 6px 8px;
  text-align: left;
}
.retention-table .num-col {
  text-align: right;
  white-space: nowrap;
}
.muted { color: #888; font-style: italic; }
.small { font-size: 13px; }
</style>
